import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowRight,
  BookmarkSimple,
  Sparkles,
  Target,
  BookOpen,
  NotebookPen,
} from '../components/Icons';
import { api, type Attempt, type Exam, type Report } from '@mastery/api-client-web';
import { useOptionalSession } from '../lib/session';
import { authURL, referralCode } from '../lib/navigation';
import { LearningGuide } from '../components/LearningGuide';
import { ErrorBox, useLoad } from '../lib/shared';
import { AttemptList } from '../components/AttemptList';
import { SubjectArt, subjects } from '../components/SubjectArt';
import { LearningValue, ProfilePreview } from '../components/LearningValue';
import { FocusCard } from '../components/LearningProfile';
import { focusTitle, trainingURL, priorityFocus } from '../lib/learning';
import { PageState } from '../components/PageState';
import { Stats } from '../components/Stats';

export function Home() {
  const user = useOptionalSession()?.user;
  return user ? <LearnerHome name={user.name} /> : <VisitorHome />;
}
function VisitorHome() {
  return (
    <>
      <section className="intelligence-hero" aria-labelledby="hero-title">
        <div className="hero-copy">
          <span className="eyebrow">KNOW YOURSELF. PRACTISE SMARTER.</span>
          <h1 id="hero-title">
            找准弱项，
            <br />
            <em>
              每一次练习
              <br className="hero-break" />
              都有方向。
            </em>
          </h1>
          <p>
            是阅读、听力的题型没掌握，
            <br />
            还是科学、历史的内容不熟悉？
            <br />
            从作答中识别强弱项，为你安排针对性加强训练。
          </p>
          <div className="hero-actions">
            <Link className="button primary" to={authURL('register', '/', referralCode())}>
              免费体验智能训练
              <ArrowRight size={21} />
            </Link>
            <Link className="editorial-link" to="/practice">
              浏览题库
              <ArrowRight size={18} />
            </Link>
          </div>
          <span className="hero-footnote">
            <Sparkles size={16} />
            注册后连续 24 小时体验 · 练习越多，方向越清楚
          </span>
        </div>
        <div className="hero-diagnosis">
          <div className="diagnosis-caption">
            <span>YOUR LEARNING MAP</span>
            <span>能力 × 内容</span>
          </div>
          <ProfilePreview />
          <div className="diagnosis-foot">
            <img src="/brand/reading.webp" width="100" height="100" alt="" />
            <p>
              认识自己的强项，
              <br />
              <strong>也找到值得突破的地方。</strong>
            </p>
          </div>
        </div>
      </section>
      <LearningValue />
      <ExamEntries />
      <SubjectEntries />
      <LearningGuide />
    </>
  );
}
function ExamEntries() {
  const exams = [
    {
      code: 'KET',
      level: 'A2 KEY',
      title: 'KET 题库',
      desc: '从阅读、写作到听力和口语，按考试结构练习。',
    },
    {
      code: 'PET',
      level: 'B1 PRELIMINARY',
      title: 'PET 题库',
      desc: '选择标准版、青少年版或 Trainer 题库开始练习。',
    },
    {
      code: 'FCE',
      level: 'B2 FIRST',
      title: 'FCE 题库',
      desc: '分开练习阅读、英语运用、写作、听力和口语。',
    },
    {
      code: 'CAE',
      level: 'C1 ADVANCED',
      title: 'CAE 题库',
      desc: '按 Test、Part 和题型进入真题测试。',
    },
  ] as const;
  return (
    <section className="home-exams" aria-labelledby="home-exams-title">
      <div className="section-heading editorial-heading">
        <div>
          <span className="eyebrow">CAMBRIDGE EXAM PRACTICE</span>
          <h2 id="home-exams-title">选择考试类型，开始真题测试。</h2>
        </div>
        <Link to="/bank-preview">
          查看全部题库 <ArrowRight size={18} />
        </Link>
      </div>
      <div className="home-exam-grid">
        {exams.map((exam) => (
          <Link className="home-exam-card" to={`/bank-preview/${exam.code}`} key={exam.code}>
            <span className="home-exam-level">{exam.level}</span>
            <strong>{exam.title}</strong>
            <p>{exam.desc}</p>
            <span className="home-exam-action">
              进入真题测试 <ArrowRight size={18} />
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
}
function LearnerHome({ name }: { name: string }) {
  const exams = useLoad<Exam[]>('/exams');
  const [params, setParams] = useSearchParams();
  const examCode =
    params.get('exam') ??
    exams.data?.find((e) => e.code === 'fce-schools')?.code ??
    exams.data?.[0]?.code ??
    '';
  const exam = exams.data?.find((e) => e.code === examCode);
  const report = useLoad<Report>(exam ? '/progress?exam=' + encodeURIComponent(examCode) : null);
  const attempts = useLoad<Attempt[]>('/attempts');
  const [error, setError] = useState<unknown>();
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const profile = report.data?.profile ?? [];
  const focus = priorityFocus(profile);
  const strength =
    profile.find((f) => f.state === 'strength' && f.dimension === 'part') ??
    profile.find((f) => f.state === 'strength');
  const initial = !report.data?.first_answers;
  const active = attempts.data?.find(
    (a) =>
      a.exam_code === examCode &&
      a.status === 'in_progress' &&
      new Date(a.deadline).getTime() > Date.now(),
  );
  async function start() {
    if (!exam) return;
    setBusy(true);
    setError(undefined);
    try {
      const a = await api.post<Attempt>('/attempts', { exam_code: exam.code, mode: 'adaptive' });
      navigate('/attempts/' + a.id);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  if ((exams.error && !exams.data) || (report.error && !report.data))
    return (
      <PageState
        title="暂时无法读取能力画像。"
        description="学习记录仍保留在账号中，请恢复连接后重试。"
        onRetry={() => {
          exams.reload();
          report.reload();
        }}
      />
    );
  return (
    <div className="learning-home">
      <div className="learning-welcome">
        <div>
          <span className="eyebrow">YOUR NEXT STEP</span>
          <h1>{name}，今天练得更有方向。</h1>
          <p>了解自己的强项，把时间用在值得突破的地方。</p>
        </div>
        <label className="exam-select">
          我的考试
          <select
            aria-label="我的考试"
            value={examCode}
            onChange={(e) => setParams({ exam: e.target.value })}
          >
            {exams.data?.map((e) => (
              <option value={e.code} key={e.code}>
                {e.name}
              </option>
            ))}
          </select>
        </label>
      </div>
      <ErrorBox
        error={error ?? report.error ?? attempts.error ?? exams.error}
        onRetry={() => {
          setError(undefined);
          report.reload();
          attempts.reload();
          exams.reload();
        }}
      />
      {!exams.loading && !exam && (
        <p role="status" className="note">
          当前考试暂不可用，请从题库选择已开放的考试。
        </p>
      )}
      <section className="next-training" aria-labelledby="next-title">
        <div className="next-training-copy">
          <span className="eyebrow">
            <Target size={17} />
            {active
              ? '接着上次的进度'
              : focus
                ? '优先突破'
                : initial
                  ? '建立你的能力画像'
                  : '让判断更有依据'}
          </span>
          <h2 id="next-title">
            {active
              ? active.title
              : focus
                ? focusTitle(focus, exams.data ?? [])
                : initial
                  ? '先做一组，发现你的学习起点。'
                  : '用同类新题，看清强项与弱项。'}
          </h2>
          <p>
            {active
              ? '已保存的答案可以继续完成。交卷后，查看解析和更新后的能力分析。'
              : focus
                ? focus.reason
                : '从阅读与听力开始，系统结合题型、内容主题和难度分析作答，为下一步训练提供依据。'}
          </p>
          <div className="hero-actions">
            {active ? (
              <Link className="button primary" to={'/attempts/' + active.id}>
                继续上次练习
                <ArrowRight size={20} />
              </Link>
            ) : focus ? (
              <Link className="button primary" to={trainingURL(focus)}>
                开始针对性加强
                <ArrowRight size={20} />
              </Link>
            ) : (
              <button
                className="primary"
                disabled={busy || report.loading || attempts.loading || !exam}
                onClick={start}
              >
                {busy
                  ? '正在为你选题…'
                  : report.loading
                    ? '正在读取学习表现…'
                    : initial
                      ? '开始能力摸底'
                      : '开始智能训练'}
                <ArrowRight size={20} />
              </button>
            )}
            <Link className="editorial-link" to={'/progress?exam=' + encodeURIComponent(examCode)}>
              查看能力分析
              <ArrowRight size={17} />
            </Link>
          </div>
        </div>
        <div className="learning-cycle">
          <span className="cycle-heading">你的进阶路径</span>
          <div>
            <span>01</span>
            <strong>智能评测</strong>
            <small>看清题型与内容表现</small>
          </div>
          <div className="current">
            <span>02</span>
            <strong>针对性加强</strong>
            <small>集中练习需要突破的部分</small>
          </div>
          <div>
            <span>03</span>
            <strong>新题检验</strong>
            <small>用新的材料观察进步</small>
          </div>
        </div>
      </section>
      <Stats report={report.data} />
      <section className="home-profile">
        <div className="section-heading editorial-heading">
          <div>
            <span className="eyebrow">WHAT TO FOCUS ON</span>
            <h2>我的强项与突破点</h2>
          </div>
          <Link to={'/progress?exam=' + encodeURIComponent(examCode)}>
            题型 × 主题分析
            <ArrowRight size={17} />
          </Link>
        </div>
        {report.loading ? (
          <p role="status">正在分析你的学习记录…</p>
        ) : (
          <div className="home-focus-grid">
            {focus && <FocusCard focus={focus} exams={exams.data ?? []} />}
            {strength && <FocusCard focus={strength} exams={exams.data ?? []} />}
            {!focus && (
              <div className="profile-empty">
                <Target size={29} weight="duotone" />
                <h3>{initial ? '你的能力画像，从第一组开始。' : '继续探索，还会看得更清楚。'}</h3>
                <p>
                  {initial
                    ? '完成练习后，这里会显示值得优先加强的题型和内容主题。'
                    : '当前还没有样本充分的优先弱项。继续做新题，扩大不同题型和主题的覆盖。'}
                </p>
                <Link
                  className="editorial-link"
                  to={'/practice?exam=' + encodeURIComponent(examCode)}
                >
                  选择练习
                  <ArrowRight size={17} />
                </Link>
              </div>
            )}
            {!strength && (
              <div className="profile-empty sage">
                <BookOpen size={29} weight="duotone" />
                <h3>找到强项，也是一种进步。</h3>
                <p>
                  同一维度积累至少 10 道首次客观题、2
                  组不同材料后，再观察你的优势。一次成绩不定义你的能力。
                </p>
              </div>
            )}
          </div>
        )}
      </section>
      <div className="learning-shortcuts">
        <Link to={'/practice?exam=' + encodeURIComponent(examCode)}>
          <BookOpen size={27} weight="duotone" />
          <div>
            <h3>题库与专项</h3>
            <p>按版本、题型、内容主题选题</p>
          </div>
          <ArrowRight size={21} />
        </Link>
        <Link to={'/mock?exam=' + encodeURIComponent(examCode)}>
          <BookmarkSimple size={27} weight="duotone" />
          <div>
            <h3>模拟检验</h3>
            <p>在完整测试中检验训练成果</p>
          </div>
          <ArrowRight size={21} />
        </Link>
        <Link to="/wrong">
          <NotebookPen size={27} weight="duotone" />
          <div>
            <h3>错题巩固</h3>
            <p>读懂解析，再用同类题加强</p>
          </div>
          <ArrowRight size={21} />
        </Link>
      </div>
      <section className="home-journal">
        <div className="section-heading">
          <h2>最近学习记录</h2>
          <Link to={'/progress?exam=' + encodeURIComponent(examCode)}>
            全部记录
            <ArrowRight size={17} />
          </Link>
        </div>
        <AttemptList
          rows={attempts.data?.filter((a) => a.exam_code === examCode).slice(0, 3) ?? []}
          loading={attempts.loading}
        />
      </section>
    </div>
  );
}
function SubjectEntries() {
  return (
    <section className="home-subjects" aria-labelledby="subjects-title">
      <div className="section-heading editorial-heading">
        <div>
          <span className="eyebrow">A COMPLETE LEARNING PATH</span>
          <h2 id="subjects-title">从专项突破，到综合运用。</h2>
        </div>
        <Link to="/practice">
          探索全部练习
          <ArrowRight size={18} />
        </Link>
      </div>
      <div className="subject-grid">
        {subjects.map((s) => (
          <Link className={'subject-entry ' + s.id} key={s.id} to={'/practice?section=' + s.id}>
            <SubjectArt section={s.id} />
            <div>
              <span className="subject-english">{s.english}</span>
              <h3>{s.title}</h3>
              <p>{s.desc}</p>
              <ArrowRight className="subject-arrow" size={21} />
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
